// "Informações do envio" (dataRaw da origem) no padrão pay2b:
// envolto num SectionCard (mesma moldura dos demais cards do detalhe)
// com abas [Informações | JSON]. "Informações" é a árvore chave→valor
// LEGÍVEL, colapsável por seção de topo (customer/subscription/
// payment…), com rótulos no estilo InfoItem (muted); "JSON" é o
// JsonViewer interativo (react-json-view) para o dado cru.
import { ChevronDownIcon, DatabaseIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { SectionCard } from "@/components/shared/detail-components"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { cn } from "@/lib/utils"

import { JsonViewer } from "./json-viewer"

const detailPrefix =
  "pages.notificationCenter.notificationDetailPage"

const isPlainObject = (
  value: unknown
): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value)

// Valor primitivo legível ("—" para nulos, sem "[object Object]").
const formatPrimitive = (value: unknown): string => {
  if (value === null || value === undefined) return "—"
  if (typeof value === "boolean")
    return value ? "true" : "false"
  return String(value)
}

// Conta itens de um nó (objeto/array) para o badge da seção.
const countEntries = (value: unknown): number => {
  if (Array.isArray(value)) return value.length
  if (isPlainObject(value)) return Object.keys(value).length
  return 0
}

// Linhas chave→valor recursivas: rótulo muted (padrão InfoItem),
// objetos aninhados indentam com uma guia à esquerda.
const PayloadEntries = ({
  value,
}: {
  value: unknown
}): React.ReactNode => {
  if (isPlainObject(value)) {
    return (
      <dl className="space-y-1.5">
        {Object.entries(value).map(([key, entry]) => (
          <div key={key} className="min-w-0 text-sm">
            {isPlainObject(entry) ||
            Array.isArray(entry) ? (
              <div className="space-y-1.5">
                <dt className="text-xs font-medium text-muted-foreground">
                  {key}
                </dt>
                <dd className="border-l border-border pl-3">
                  <PayloadEntries value={entry} />
                </dd>
              </div>
            ) : (
              <div className="flex items-baseline justify-between gap-3">
                <dt className="shrink-0 text-xs font-medium text-muted-foreground">
                  {key}
                </dt>
                <dd className="min-w-0 break-all text-right tabular-nums">
                  {formatPrimitive(entry)}
                </dd>
              </div>
            )}
          </div>
        ))}
      </dl>
    )
  }
  if (Array.isArray(value)) {
    return (
      <ol className="space-y-1.5">
        {value.map((entry, index) => (
          <li
            key={`payload-item-${index}`}
            className="text-sm"
          >
            <PayloadEntries value={entry} />
          </li>
        ))}
      </ol>
    )
  }
  return (
    <span className="text-sm">
      {formatPrimitive(value)}
    </span>
  )
}

// Uma seção de topo do payload (customer, subscription…): cabeçalho
// clicável com chevron animado + contador de campos.
const PayloadSection = ({
  name,
  value,
}: {
  name: string
  value: unknown
}) => {
  const count = countEntries(value)
  return (
    // Encolhido por padrão: o usuário abre só a seção que quer inspecionar.
    <Collapsible className="group overflow-hidden rounded-md border border-border">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 bg-muted/40 px-3 py-2 text-left transition-colors hover:bg-muted/70">
        <span className="text-sm font-medium">{name}</span>
        <span className="flex items-center gap-2">
          {count > 0 && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
              {count}
            </span>
          )}
          <ChevronDownIcon
            className={cn(
              "size-4 text-muted-foreground transition-transform",
              "group-data-[state=closed]:-rotate-90"
            )}
            aria-hidden="true"
          />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 py-2.5">
        <PayloadEntries value={value} />
      </CollapsibleContent>
    </Collapsible>
  )
}

export const NotificationPayloadTree = ({
  payload,
  sizeBytes,
}: {
  payload: Record<string, unknown>
  // Tamanho persistido do payload (dataInformation.sizeBytes) —
  // exibido no header do JsonViewer quando disponível.
  sizeBytes?: number
}) => {
  const { t } = useTranslation()

  return (
    <SectionCard
      title={t(`${detailPrefix}.dataInformation`)}
      icon={<DatabaseIcon size={16} />}
    >
      <Tabs defaultValue="info" className="space-y-3">
        <TabsList>
          <TabsTrigger value="info">
            {t(`${detailPrefix}.dataTabs.info`)}
          </TabsTrigger>
          <TabsTrigger value="json">
            {t(`${detailPrefix}.dataTabs.json`)}
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="info"
          className="grid gap-2 sm:grid-cols-2"
        >
          {Object.entries(payload).map(
            ([section, value]) => (
              <PayloadSection
                key={section}
                name={section}
                value={value}
              />
            )
          )}
        </TabsContent>
        <TabsContent value="json">
          <JsonViewer
            value={payload}
            sizeBytes={sizeBytes}
          />
        </TabsContent>
      </Tabs>
    </SectionCard>
  )
}
